import React, { useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import {
   initiateTransferApi,
   cancelTransferApi,
   listTransfersApi,
   type ApiDeviceTransfer,
} from '@/api/vendo';
import type { VendoDevice } from '@/pages/Admin/Vendo';
import useAuthStore from '@/store/authStore';
import ClientAccessSection from '@/components/Admin/Prompt/ClientAccessSection';

interface Props {
   data: { device: VendoDevice };
}

type Tab = 'free' | 'full-cash' | 'sub-login';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Display-only formatter: K3MXF7QR → K3MX-F7QR (server stores no dash). */
const formatClaimCode = (raw: string): string => {
   const clean = raw.replace(/[\s-]/g, '').toUpperCase();
   if (clean.length !== 8) return raw;
   return `${clean.slice(0, 4)}-${clean.slice(4)}`;
};

const SellDevice = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const { user: authUser } = useAuthStore() as {
      user?: { email?: string } | null;
   };
   const sellerEmail = authUser?.email || '';
   const queryClient = useQueryClient();
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const { device } = data;

   const [tab, setTab] = useState<Tab>('full-cash');
   const [toEmail, setToEmail] = useState('');
   const [amount, setAmount] = useState('');
   const [note, setNote] = useState('');
   const [withClaimCode, setWithClaimCode] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [pending, setPending] = useState<ApiDeviceTransfer | null | undefined>(undefined);
   const [createdLink, setCreatedLink] = useState<{
      url: string;
      email: string;
      amount?: number;
      paymentType: Tab;
      expiresAt: string;
      claimCode: string | null;
   } | null>(null);
   const [linkCopied, setLinkCopied] = useState(false);
   const [codeCopied, setCodeCopied] = useState(false);

   // Surface any pending transfer for this device so the operator can
   // cancel and re-initiate instead of getting a confusing 409.
   React.useEffect(() => {
      let cancelled = false;
      (async () => {
         try {
            const outgoing = await listTransfersApi('outgoing');
            if (cancelled) return;
            const match = outgoing.find(
               (t) =>
                  (typeof t.device === 'object' ? t.device._id : t.device) ===
                     device._id && t.status === 'pending'
            );
            setPending(match ?? null);
         } catch {
            if (!cancelled) setPending(null);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [device._id]);

   const handleSubmit = async () => {
      if (isSubmitting) return;
      const normalizedEmail = toEmail.trim().toLowerCase();
      if (!EMAIL_RE.test(normalizedEmail)) {
         toast.error('Buyer email looks wrong');
         return;
      }
      let parsedAmount: number | undefined;
      if (tab === 'full-cash') {
         parsedAmount = Number(amount);
         if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            toast.error('Enter the sale amount in pesos');
            return;
         }
      }
      setIsSubmitting(true);
      try {
         const res = await initiateTransferApi(device._id, {
            toEmail: normalizedEmail,
            paymentType: tab,
            ...(tab === 'full-cash' ? { fullCashAmount: parsedAmount } : {}),
            ...(note.trim() ? { note: note.trim() } : {}),
            ...(withClaimCode ? { generateCode: true } : {}),
         });
         const acceptLink = `${window.location.origin}/transfers/handoff?token=${encodeURIComponent(res.signupToken)}`;
         // Try clipboard write — but don't depend on it. The success view
         // shows the link inline regardless, so the seller can copy
         // manually if the auto-copy fails (some browsers block it).
         try {
            await navigator.clipboard.writeText(acceptLink);
            setLinkCopied(true);
         } catch {
            setLinkCopied(false);
         }
         setCreatedLink({
            url: acceptLink,
            email: normalizedEmail,
            amount: parsedAmount,
            paymentType: tab,
            expiresAt: res.expiresAt,
            claimCode: res.claimCode,
         });
         refetchDevices();
         // Ownership tab in DeviceSettings polls the outgoing list to
         // decide whether to show "Sell or transfer" vs "Transfer in
         // progress". Invalidate so the next Settings open reflects the
         // new pending transfer instead of stale-cached "no pending".
         queryClient.invalidateQueries({ queryKey: ['transfers'] });
      } catch (err: any) {
         const msg =
            err?.response?.data?.error || 'Could not start the transfer';
         toast.error(msg);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleCopyLink = async () => {
      if (!createdLink) return;
      try {
         await navigator.clipboard.writeText(createdLink.url);
         setLinkCopied(true);
         toast.success('Link copied');
      } catch {
         toast.error('Copy failed — select the link manually');
      }
   };

   const handleCancelPending = async () => {
      try {
         await cancelTransferApi(device._id);
         toast.success('Pending transfer cancelled');
         setPending(null);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Cancel failed');
      }
   };

   if (createdLink) {
      return (
         <Wrapper>
            <Summary>
               <DeviceTitle>{device.name}</DeviceTitle>
               <DeviceMeta>{device.id}</DeviceMeta>
            </Summary>

            <SuccessBanner>
               <SuccessIcon>✓</SuccessIcon>
               <SuccessTitle>Transfer request successfully sent</SuccessTitle>
               <SuccessBody>
                  {createdLink.paymentType === 'full-cash'
                     ? `Paid · ₱${(createdLink.amount ?? 0).toLocaleString()}`
                     : 'Plain Transfer'}
                  {' · '}
                  <strong>{createdLink.email}</strong>
                  {' · expires '}
                  {new Date(createdLink.expiresAt).toLocaleDateString()}
               </SuccessBody>
            </SuccessBanner>

            <LinkSection>
               <LinkLabel>Accept link — send this to the buyer</LinkLabel>
               <LinkRow>
                  <LinkInput readOnly value={createdLink.url} onFocus={(e) => e.currentTarget.select()} />
                  <CopyBtn type="button" onClick={handleCopyLink}>
                     {linkCopied ? 'Copied ✓' : 'Copy'}
                  </CopyBtn>
               </LinkRow>
               <LinkHelp>
                  The buyer needs an existing SmartFi account with this email.
                  When they open the link, a Review modal opens on their
                  Devices page with Accept / Reject actions.
               </LinkHelp>
            </LinkSection>

            {createdLink.claimCode && (
               <CodeSection>
                  <LinkLabel>Or — read this code to the buyer</LinkLabel>
                  <CodeBox>
                     <CodeDisplay>
                        {formatClaimCode(createdLink.claimCode)}
                     </CodeDisplay>
                     <CopyBtn
                        type="button"
                        onClick={async () => {
                           try {
                              await navigator.clipboard.writeText(
                                 createdLink.claimCode!
                              );
                              setCodeCopied(true);
                              toast.success('Code copied');
                           } catch {
                              toast.error('Copy failed — select the code manually');
                           }
                        }}
                     >
                        {codeCopied ? 'Copied ✓' : 'Copy'}
                     </CopyBtn>
                  </CodeBox>
                  <LinkHelp>
                     The buyer opens <strong>Devices → Claim a transfer</strong>{' '}
                     and types this code. Works only with the email above.
                  </LinkHelp>
               </CodeSection>
            )}

            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Done" type="normal" size="small" bgcolor={colors.PRIMARY} textcolor={colors.WHITE} />
               </div>
            </BtnRow>
         </Wrapper>
      );
   }

   return (
      <Wrapper>
         <Summary>
            <DeviceTitle>{device.name}</DeviceTitle>
            <DeviceMeta>{device.id}</DeviceMeta>
         </Summary>

         {pending && (
            <PendingBanner>
               <PendingHeader>Pending transfer to {pending.toEmail}</PendingHeader>
               <PendingMeta>
                  {pending.paymentType === 'full-cash'
                     ? `Paid · ₱${(pending.fullCashAmount ?? 0).toLocaleString()}`
                     : 'Plain Transfer'}
                  {' · expires '}
                  {new Date(pending.expiresAt).toLocaleDateString()}
               </PendingMeta>
               <CancelLink onClick={handleCancelPending}>
                  Cancel pending transfer
               </CancelLink>
            </PendingBanner>
         )}

         {!pending && (
            <>
               <TabBar>
                  <TabBtn
                     $active={tab === 'full-cash'}
                     onClick={() => setTab('full-cash')}
                  >
                     Paid
                  </TabBtn>
                  <TabBtn $active={tab === 'free'} onClick={() => setTab('free')}>
                     Plain Transfer
                  </TabBtn>
                  <TabBtn
                     $active={tab === 'sub-login'}
                     onClick={() => setTab('sub-login')}
                  >
                     Sub login
                  </TabBtn>
                  <InstallmentDisabled title="Installment plans land in a later release">
                     Installment (soon)
                  </InstallmentDisabled>
               </TabBar>

               {tab === 'sub-login' && (
                  <>
                     {/* No email, no transfer — you keep ownership; the
                         buyer gets a simple login for the basic portal. */}
                     <ClientAccessSection
                        deviceId={device._id}
                        sellerEmail={sellerEmail}
                     />
                     <BtnRow>
                        <div onClick={handleClose}>
                           <Btn title="Close" type="outline" size="small" />
                        </div>
                     </BtnRow>
                  </>
               )}

               {tab !== 'sub-login' && (
               <>
               <FieldStack>
                  <FieldLabel>Buyer's email</FieldLabel>
                  <Input
                     type="email"
                     placeholder="buyer@example.com"
                     value={toEmail}
                     onChange={(e) => setToEmail(e.target.value)}
                  />

                  {tab === 'full-cash' && (
                     <>
                        <FieldLabel>Sale amount (₱)</FieldLabel>
                        <Input
                           type="number"
                           min="1"
                           step="1"
                           placeholder="8500"
                           value={amount}
                           onChange={(e) => setAmount(e.target.value)}
                        />
                        <FieldHint>
                           Money is collected offline (cash / GCash / bank).
                           SmartFi just records the sale for the audit log.
                        </FieldHint>
                     </>
                  )}

                  <FieldLabel>Note (optional)</FieldLabel>
                  <Input
                     placeholder="Short label visible only to you and the buyer"
                     value={note}
                     onChange={(e) => setNote(e.target.value)}
                  />

                  <CodeOptRow>
                     <CodeOptCheckbox
                        type="checkbox"
                        checked={withClaimCode}
                        onChange={(e) => setWithClaimCode(e.target.checked)}
                     />
                     <CodeOptLabel>
                        <CodeOptTitle>
                           Also generate a claim code
                        </CodeOptTitle>
                        <CodeOptHint>
                           For buyers who don't check email — you read the
                           8-character code to them, they type it in.
                        </CodeOptHint>
                     </CodeOptLabel>
                  </CodeOptRow>
               </FieldStack>

               <Notice>
                  We'll email the buyer an accept link and also show it here
                  for you to copy. They click → confirm → device moves to
                  their dashboard.
               </Notice>

               <BtnRow>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <div onClick={handleSubmit}>
                     <Btn
                        title={isSubmitting ? 'Submitting…' : 'Submit Transfer'}
                        type={isSubmitting ? 'disabled' : 'normal'}
                        size="small"
                        bgcolor={isSubmitting ? colors.GRAY10 : colors.PRIMARY}
                        textcolor={isSubmitting ? colors.GRAY1 : colors.WHITE}
                     />
                  </div>
               </BtnRow>
               </>
               )}
            </>
         )}
      </Wrapper>
   );
};

export default SellDevice;

const Wrapper = styled.div`
   width: 520px;
   max-width: 100%;
   padding: 4px 0 0;
`;

const Summary = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   margin-bottom: 16px;
`;

const DeviceTitle = styled.div`
   font-size: 16px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const DeviceMeta = styled.div`
   font-size: 11px;
   font-family: 'SF Mono', Menlo, monospace;
   color: ${colors.GRAY5};
`;

const SuccessBanner = styled.div`
   display: grid;
   grid-template-columns: 28px 1fr;
   grid-template-rows: auto auto;
   column-gap: 12px;
   align-items: center;
   border: 1px solid ${colors.SUCCESS}40;
   background: ${colors.SUCCESS}10;
   border-radius: 10px;
   padding: 14px 16px;
   margin-bottom: 14px;
`;

const SuccessIcon = styled.div`
   grid-row: 1 / 3;
   width: 28px;
   height: 28px;
   border-radius: 50%;
   background: ${colors.SUCCESS};
   color: ${colors.WHITE};
   display: flex;
   align-items: center;
   justify-content: center;
   font-weight: 700;
`;

const SuccessTitle = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.SUCCESS};
`;

const SuccessBody = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   strong {
      color: ${colors.GRAY_DARK};
   }
`;

const LinkSection = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin-bottom: 16px;
`;

const CodeSection = styled(LinkSection)``;

const CodeBox = styled.div`
   display: flex;
   align-items: stretch;
   gap: 8px;
   background: ${colors.PRIMARY}08;
   border: 1px dashed ${colors.PRIMARY}50;
   border-radius: 10px;
   padding: 12px;
`;

const CodeDisplay = styled.div`
   flex: 1;
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 22px;
   font-weight: 700;
   letter-spacing: 0.18em;
   color: ${colors.PRIMARY};
   text-align: center;
   padding: 8px 10px;
   user-select: all;
`;

const CodeOptRow = styled.label`
   display: flex;
   align-items: flex-start;
   gap: 10px;
   padding: 10px 12px;
   margin-top: 8px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   cursor: pointer;
`;

const CodeOptCheckbox = styled.input`
   margin-top: 2px;
   cursor: pointer;
`;

const CodeOptLabel = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   cursor: pointer;
`;

const CodeOptTitle = styled.div`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY_DARK};
`;

const CodeOptHint = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
`;

const LinkLabel = styled.label`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
`;

const LinkRow = styled.div`
   display: flex;
   gap: 8px;
`;

const LinkInput = styled.input`
   flex: 1;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY11};
   font-family: 'SF Mono', Menlo, monospace;
   font-size: 11px;
   color: ${colors.GRAY_DARK};
   background: ${colors.GRAY14};
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const CopyBtn = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 0 16px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover {
      opacity: 0.92;
   }
`;

const LinkHelp = styled.div`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   code {
      font-family: 'SF Mono', Menlo, monospace;
      font-size: 10px;
      background: ${colors.GRAY14};
      padding: 1px 4px;
      border-radius: 3px;
   }
`;

const PendingBanner = styled.div`
   border: 1px solid ${colors.WARNING}40;
   background: ${colors.WARNING}10;
   border-radius: 10px;
   padding: 14px 16px;
   display: flex;
   flex-direction: column;
   gap: 4px;
   margin-bottom: 16px;
`;

const PendingHeader = styled.div`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.WARNING};
`;

const PendingMeta = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
`;

const CancelLink = styled.button`
   margin-top: 6px;
   align-self: flex-start;
   background: none;
   border: none;
   padding: 0;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.DANGER};
   cursor: pointer;
   text-decoration: underline;
`;

const TabBar = styled.div`
   display: flex;
   gap: 4px;
   border-bottom: 1px solid ${colors.GRAY12};
   margin-bottom: 14px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
   background: none;
   border: none;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: ${({ $active }) => ($active ? 700 : 500)};
   color: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY3)};
   border-bottom: 2px solid
      ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   margin-bottom: -1px;
   cursor: pointer;
   &:hover {
      color: ${colors.PRIMARY};
   }
`;

const InstallmentDisabled = styled.span`
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 500;
   color: ${colors.GRAY8};
   cursor: not-allowed;
`;

const FieldStack = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   margin-bottom: 14px;
`;

const FieldLabel = styled.label`
   font-size: 11px;
   font-weight: 700;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   margin-top: 4px;
`;

const Input = styled.input`
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY11};
   font-size: 13px;
   color: ${colors.GRAY_DARK};
   background: ${colors.WHITE};
   &:focus {
      outline: none;
      border-color: ${colors.PRIMARY};
   }
`;

const FieldHint = styled.div`
   font-size: 10px;
   color: ${colors.GRAY5};
   font-style: italic;
`;

const Notice = styled.div`
   background: ${colors.GRAY14};
   border-radius: 8px;
   padding: 10px 12px;
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
   margin-bottom: 14px;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
`;
